// Selector genérico de catálogos chicos (ESTADO_SERVICIO, ESTADO_CLIENTE, etc.).
// La categoría completa entra en una sola página y se filtra localmente por label.
// Los ids llegan como string del API (bigint serializado): se normalizan a
// number para que el value del form haga match con las opciones.
import { Select } from 'antd';
import { useCatalogos } from '../features/catalogos/api/useCatalogos';
import type { CatalogoResponse } from '@aid/shared';
import { useT } from '../locales';
import { filterOptionByLabel } from '../lib/normalizeSearchText';

interface SelectAccessibilityProps {
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

interface CatalogoSelectProps extends SelectAccessibilityProps {
  category: string;
  value?: number | null;
  onChange?: (id: number | undefined) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  /** Filtra los items del catálogo antes de armar las opciones. */
  filterOptions?: (c: CatalogoResponse) => boolean;
}

export function CatalogoSelect({
  category,
  value,
  onChange,
  placeholder,
  allowClear = true,
  disabled,
  filterOptions,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: CatalogoSelectProps) {
  const t = useT();
  const { data, isLoading, error } = useCatalogos(category);

  const items = filterOptions ? (data ?? []).filter(filterOptions) : (data ?? []);
  const options = items.map((c) => ({
    value: Number(c.id),
    label: c.value ?? c.key ?? `#${c.id}`,
  }));

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      style={{ width: '100%' }}
      loading={isLoading}
      disabled={disabled}
      allowClear={allowClear}
      showSearch={{ filterOption: filterOptionByLabel }}
      defaultActiveFirstOption={false}
      notFoundContent={
        isLoading ? t('common.loading') : error ? t('common.error') : t('common.noResults')
      }
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={options}
    />
  );
}
